'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { motion } from 'framer-motion'
import { AlertCircle, Plus } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import { SpeciesIcon } from '@/customer/components/pet/species-icon'
import { usePetIssues } from '@/customer/hooks/use-pet-issues'

/**
 * Tile shell. Covers both `data-checked` (radio) and `data-active`
 * (tabs) so the same class list works for either wrapper element.
 */
export const petTileStyles = cva(
  [
    'group/pet-tile flex aspect-auto size-auto min-w-0 flex-none cursor-pointer items-center gap-1.5 rounded-lg border-2 border-input bg-neutral-100 p-2 text-left transition-colors',
    'hover:border-foreground',
    'data-checked:border-foreground data-active:border-foreground',
    'data-disabled:cursor-not-allowed data-disabled:opacity-50 data-disabled:hover:border-input',
  ],
  {
    variants: {
      color: {
        yellow: 'hover:bg-yellow/30 data-checked:bg-yellow data-active:bg-yellow',
        blue: 'hover:bg-blue/30 data-checked:bg-blue data-active:bg-blue',
        pink: 'hover:bg-pink/30 data-checked:bg-pink data-active:bg-pink',
        'baby-blue': 'hover:bg-baby-blue/30 data-checked:bg-baby-blue data-active:bg-baby-blue',
        green: 'hover:bg-green/30 data-checked:bg-green data-active:bg-green',
        orange: 'hover:bg-orange/30 data-checked:bg-orange data-active:bg-orange',
        neutral: 'hover:bg-neutral-200 data-checked:bg-neutral-200 data-active:bg-neutral-200',
      },
    },
    defaultVariants: { color: 'yellow' },
  }
)

const monogramStyles = cva(
  'flex size-11 shrink-0 items-center justify-center rounded-lg border-2 border-foreground text-foreground',
  {
    variants: {
      color: {
        yellow: 'bg-yellow',
        blue: 'bg-blue',
        pink: 'bg-pink',
        'baby-blue': 'bg-baby-blue',
        green: 'bg-green',
        orange: 'bg-orange',
        neutral: 'bg-neutral-200',
      },
    },
    defaultVariants: { color: 'pink' },
  }
)

export type PetTileColor = NonNullable<VariantProps<typeof petTileStyles>['color']>
export type PetTileMonogramColor = NonNullable<VariantProps<typeof monogramStyles>['color']>

type MonogramVariant = 'filled' | 'bare'

/**
 * Pet tile content - species-icon monogram + name + optional breed +
 * issue indicator. Renders the innards only; the caller owns the
 * wrapper element and applies `petTileStyles` to it.
 */
export function PetTileContent({
  pet,
  isSelected = false,
  monogramColor = 'pink',
  monogramVariant = 'filled',
  showBreed = true,
}: {
  pet: PublicPatient
  isSelected?: boolean
  monogramColor?: PetTileMonogramColor
  monogramVariant?: MonogramVariant
  showBreed?: boolean
}) {
  const { issue } = usePetIssues(pet.id)

  return (
    <>
      <motion.span
        initial={false}
        animate={isSelected ? 'selected' : 'idle'}
        variants={{
          idle: { scale: 1, rotate: 0 },
          selected: { scale: [1, 1.15, 0.95, 1.05, 1], rotate: [0, -8, 6, -3, 0] },
        }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={
          monogramVariant === 'bare'
            ? 'flex shrink-0 items-center justify-center text-foreground'
            : monogramStyles({ color: monogramColor })
        }
      >
        <SpeciesIcon
          species={pet.species}
          className={monogramVariant === 'bare' ? 'size-7' : 'size-6'}
          aria-hidden
        />
      </motion.span>
      <span className="flex min-w-0 flex-col">
        <span className="text-button">{pet.name}</span>
        {showBreed && pet.breed ? (
          <span className="mt-1 truncate text-fine-print text-muted-foreground">{pet.breed}</span>
        ) : null}
      </span>
      {issue?.type === 'incomplete_subscription' ? (
        <AlertCircle
          className="-ml-0.5 size-3 -translate-y-2.5 shrink-0 text-warning"
          aria-hidden
        />
      ) : null}
    </>
  )
}

/** Add-pet tile content - plus icon + optional label. */
export function AddPetTileContent({
  label,
  monogramVariant = 'filled',
}: {
  label?: string
  monogramVariant?: MonogramVariant
}) {
  const isBare = monogramVariant === 'bare'
  return (
    <>
      <span
        className={
          isBare
            ? 'flex shrink-0 items-center justify-center text-foreground'
            : monogramStyles({ color: 'neutral' })
        }
      >
        <Plus className={isBare ? 'size-7' : 'size-6'} aria-hidden />
      </span>
      {label && <span className="text-body-md font-bold uppercase leading-none">{label}</span>}
    </>
  )
}
